import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Grow, Stack, Tab, Tabs } from '@gafe/common-ui';

import { getTabsToRender } from '@/components/layout/Header/Navigation/constants';
import { useActivityFilters } from '@/hooks/useActivityFilters/useActivityFilters';
import { TabType } from '@/pages/Dashboard/ActivityList/helpers';

import styles from './Navigation.module.scss';

export type NavigationProps = {
  isMobile?: boolean;
};

export const Navigation = ({ isMobile = false }: NavigationProps) => {
  const history = useHistory();
  const location = useLocation();

  const [filters, setFilters] = useActivityFilters();

  const [currentTab, setCurrentTab] = useState(filters.type || 0);

  const isDashboardPage = /^\/dashboard(\/)?(\?.*)*$/.test(location.pathname);
  const isDetailsPage = /^\/dashboard\/(\d)-([\d\w]+)$/.test(location.pathname);

  useEffect(() => {
    try {
      if (isDetailsPage) {
        const [, , activity] = location.pathname.split('/');

        const [activityType] = activity.split('-');

        const parsedActivityType = Number(activityType ?? 0);

        // Activity types mapping:
        //    Home     | Proposals | Projects | Orders
        // [undefined,    [1, 2],      [0],       [3]]

        switch (parsedActivityType) {
          case 1:
          case 2:
            setCurrentTab(TabType['Leads']);
            break;

          case 3:
            setCurrentTab(TabType['Orders']);
            break;

          case 0:
            setCurrentTab(TabType['Projects']);
            break;

          default:
            setCurrentTab(TabType['Home']);
        }

        return;
      }

      // If not details page, then we are in the dashboard
      setCurrentTab(Number(filters.type ?? 0));
    } catch {
      // Fallback
      setCurrentTab(0);
    }
  }, []);

  const handleOnActiveChange = (nextTab: string | undefined) => {
    const tabType = TabType[nextTab ?? 'Home'];

    setCurrentTab(tabType);

    if (!isDashboardPage) {
      history.push({
        pathname: '/dashboard',
        search: `?type=${tabType}`,
      });

      return;
    }
    setFilters({ ...filters, type: tabType });
  };

  const getTab = (name: string, icon: JSX.Element) => {
    return (
      <Tab border={isMobile ? 'top' : 'bottom'} key={name} name={name}>
        <Stack
          align={{ items: 'center' }}
          gap="2xs"
          justify={{ content: 'space-between' }}
          orientation={{ desktop: 'horizontal', mobile: 'vertical' }}
        >
          {icon}
          {name}
        </Stack>
      </Tab>
    );
  };

  return (
    <section className={styles.navbar} data-mobile={isMobile}>
      <Tabs
        active={TabType[currentTab]}
        className={styles.tabs}
        data-testId="navigation-v2"
        onActiveChange={handleOnActiveChange}
      >
        {getTabsToRender.map(({ name, icon }) => {
          const tab = getTab(name, icon);

          if (isMobile) {
            return <Grow key={name}>{tab}</Grow>;
          }

          return tab;
        })}
      </Tabs>
    </section>
  );
};
